import { useEffect, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, Button, ConfirmDialog, PillButton, SaveFlash, apiErrorMessage, brandDisplayName, formatMoney } from '@aralere/shared-ui';
import type { ImmediateInvoiceRef, PendingCreditTrigger, PendingCreditsResponse } from '@aralere/shared-types';
import { CreateLicenseExpenseDialog } from './CreateLicenseExpenseDialog';
import { PendingCreditsDialog, refundDeepLink, resolvedSummary } from './PendingCreditsDialog';
import type {
  Business,
  LicenseExpensePrompt,
  Service,
  Package,
  RetentionModel,
  RevivableAssignment,
  ClientService,
  ClientServiceWithCatalog,
  ClientPackageWithContents,
  CredentialHandoffMeta,
  NonPaymentSuspendedUnit,
} from '@aralere/shared-types';

const money = (n: number): string => `$${n.toFixed(2)}`;
// This panel lives inside the client-edit <form>; stop Enter in its inputs from submitting that form.
const stopEnter = (e: KeyboardEvent): void => {
  if (e.key === 'Enter') e.preventDefault();
};

// ── one-time credential handoff (08 Q32, s39) — admin issue/void for temp-password manual services ──
function readHandoff(cs: ClientService): CredentialHandoffMeta | null {
  const raw = cs.metadata?.credentialHandoff;
  if (!raw || typeof raw !== 'object') return null;
  const bag = raw as CredentialHandoffMeta;
  return typeof bag.id === 'string' && typeof bag.expiresAt === 'string' ? bag : null;
}
type CredState = 'pending' | 'revealed' | 'voided' | 'expired';
function credState(bag: CredentialHandoffMeta): CredState {
  if (bag.consumedAt) return 'revealed';
  if (bag.voidedAt) return 'voided';
  return Date.now() >= Date.parse(bag.expiresAt) ? 'expired' : 'pending';
}
// Client-side generator so the operator can set the SAME value at the vendor; ambiguous glyphs
// (I/l/1, O/0) excluded. The value only ever travels in the issue request, straight to the vault.
function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
  const buf = new Uint32Array(20);
  crypto.getRandomValues(buf);
  return Array.from(buf, (n) => chars[n % chars.length]).join('');
}

type OverrideForm = {
  price: string;
  cost: string;
  billingEntity: string; // '' = catalog default
  suppress: boolean;
  // lifecycle (s30 item 5) — per-client
  autoCancelDays: string; // '' = inherit the catalog value
  termEndsAt: string; // YYYY-MM-DD or '' (annual-term services only)
};

type PkgEditForm = {
  price: string;
  seats: string;
  billingEntity: string; // '' = package default
};

/**
 * Per-client service + package assignment (Stage 2, spec §5.2/§5.9) — the admin management layer over
 * the `clientServices` / `clientPackages` rows. Packages sit above standalone services; a package's
 * contained services materialize as child `clientService` rows (billedByPackage), so they're shown
 * UNDER their package and filtered out of the standalone list (nothing double-shows). Assignment is
 * admin-assigned (no consent prompt); no billing/provisioning happens here (Stage 4 / #11).
 */
export function ClientServicesPanel({
  clientId,
  clientName,
  businesses,
  onChanged,
}: {
  clientId: string;
  /** The client's display name for the lapse dialog's sentences (04 #160 WS-C M2b). */
  clientName?: string;
  businesses: Business[];
  // Fired after any assign/suspend/reactivate/cancel/switch/remove so the parent can refresh its
  // (otherwise-stale) client list counts (04 #G / Option A).
  onChanged?: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  // 04 #160 WS-C M2b (02i §8 DP-13): the lapse dialog, raised by a cancel / removal whose response says the
  // client is now left with no positive line while credit is still owed. Closing it keeps carrying.
  const [pendingCredits, setPendingCredits] = useState<PendingCreditsResponse | null>(null);
  const [pendingTrigger, setPendingTrigger] = useState<PendingCreditTrigger>('cancel');
  // ── services ──
  const [items, setItems] = useState<ClientServiceWithCatalog[] | null>(null);
  // Cancelled rows a re-assign would REVIVE (s30 item 5) — drives the data-loss caveat on a
  // long-cancelled revive, without listing cancelled rows in the panel.
  const [revivable, setRevivable] = useState<RevivableAssignment[]>([]);
  const [catalog, setCatalog] = useState<Service[] | null>(null);
  // The client's brand (from GET mgmt-client-services, s30 item 6) — drives the assign-list
  // brand-availability filter. Null until known; the server enforces on assign regardless.
  const [clientBusinessId, setClientBusinessId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // NON-BLOCKING seat notice (s43 §1.2): set from an assign response's seatWarning(s) — the
  // operation already succeeded; this is informational only. Cleared on the next action.
  const [seatNotice, setSeatNotice] = useState<string | null>(null);
  // SELF-ASSIGN cost prompt (slice 2, s43 record §3): set from an assign response's
  // licenseExpensePrompt (house client + nothing links the service) — opens the pre-filled
  // license-expense create dialog. Never a silent auto-create.
  const [licensePrompt, setLicensePrompt] = useState<LicenseExpensePrompt | null>(null);
  const [licenseCreated, setLicenseCreated] = useState<string | null>(null);

  const [assignId, setAssignId] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<OverrideForm | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  const [cancelTarget, setCancelTarget] = useState<ClientServiceWithCatalog | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  // one-time credential handoff (08 Q32) — issue modal + void confirm
  const [credTarget, setCredTarget] = useState<ClientServiceWithCatalog | null>(null);
  const [credUsername, setCredUsername] = useState('');
  const [credPassword, setCredPassword] = useState('');
  const [credDays, setCredDays] = useState('7');
  const [credBusy, setCredBusy] = useState(false);
  const [credError, setCredError] = useState<string | null>(null);
  const [voidCredTarget, setVoidCredTarget] = useState<ClientServiceWithCatalog | null>(null);
  const [voidCredBusy, setVoidCredBusy] = useState(false);

  // ── packages ──
  const [packages, setPackages] = useState<ClientPackageWithContents[] | null>(null);
  const [pkgCatalog, setPkgCatalog] = useState<Package[]>([]);

  const [assignPkgId, setAssignPkgId] = useState('');
  const [assignPkgBusy, setAssignPkgBusy] = useState(false);

  const [pkgEditingId, setPkgEditingId] = useState<string | null>(null);
  const [pkgEditForm, setPkgEditForm] = useState<PkgEditForm | null>(null);
  const [pkgRowBusy, setPkgRowBusy] = useState<string | null>(null);

  const [switchId, setSwitchId] = useState<string | null>(null); // clientPackage id being switched
  const [switchToId, setSwitchToId] = useState('');

  const [removePkgTarget, setRemovePkgTarget] = useState<ClientPackageWithContents | null>(null);
  const [removePkgBusy, setRemovePkgBusy] = useState(false);

  // Long-inactive reactivate caveat (fix-spec #10): replaces window.confirm with the in-app ConfirmDialog.
  // Holds the days, what's being reactivated, and the deferred action to run once confirmed.
  const [reactivateCaveat, setReactivateCaveat] = useState<
    { days: number; kind: 'service' | 'package'; retention: RetentionModel | null; revive: boolean; run: () => Promise<void> } | null
  >(null);

  // WS2: non-payment-suspended units (standalone services AND package members — the latter don't render
  // as rows above, closing the Q49 gap) + the admin resume-picker selection. Keyed svc:<id> / pkg:<id>.
  const [npUnits, setNpUnits] = useState<NonPaymentSuspendedUnit[]>([]);
  const [npSelected, setNpSelected] = useState<Set<string>>(new Set());
  // 04 #118: WHICH resume action is in flight, not merely THAT one is - so only the clicked button
  // spins. Mirrors this file's existing `rowBusy` / `pkgRowBusy` discriminators rather than a boolean.
  const [npBusy, setNpBusy] = useState<null | 'selected' | 'all'>(null);
  const [npError, setNpError] = useState<string | null>(null);
  // 04 #118: the completion signal. It must live OUTSIDE the resume card, because a successful resume
  // empties `npUnits` and the card unmounts - a flash rendered inside it would show for zero frames.
  const [npFlash, setNpFlash] = useState<string | null>(null);
  // 04 #45 (02g #45 O3; the 04 #86 toast): the immediate first-month invoice a start operation issued.
  const [immediateFlash, setImmediateFlash] = useState<string | null>(null);
  const noteImmediate = (ref: ImmediateInvoiceRef | null | undefined): void => {
    if (ref) setImmediateFlash(`Issued at assignment: ${ref.documentNumber ?? ref.id} for ${formatMoney(ref.total)} (first-month billing: immediate).`);
  };
  const npKey = (u: NonPaymentSuspendedUnit): string => (u.kind === 'service' ? `svc:${u.serviceId}` : `pkg:${u.packageId}`);

  const brandName = (id: string): string => brandDisplayName(businesses.find((b) => b.id === id)?.name ?? id);

  const reloadItems = async (): Promise<void> => {
    const res = await api.admin.listClientServices(clientId);
    setItems(res.items);
    setRevivable(res.revivable ?? []);
    setClientBusinessId(res.businessId ?? null);
  };
  const reloadPackages = async (): Promise<void> => {
    const res = await api.admin.listClientPackages(clientId);
    setPackages(res.items);
  };
  // A package op creates/adopts/reverts child clientService rows, so refresh BOTH lists.
  const reloadAll = async (): Promise<void> => {
    await Promise.all([reloadItems(), reloadPackages()]);
  };

  // WS2 — non-payment resume surface.
  const reloadNonPayment = async (): Promise<void> => {
    try {
      const res = await api.admin.listNonPaymentSuspended(clientId);
      setNpUnits(res.units);
      setNpSelected(new Set());
    } catch {
      setNpUnits([]); // non-blocking: the resume card simply won't show
    }
  };
  const toggleNp = (key: string): void =>
    setNpSelected((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const doResume = async (all: boolean): Promise<void> => {
    const serviceIds: string[] = [];
    const packageIds: string[] = [];
    if (!all) {
      for (const u of npUnits) {
        if (!npSelected.has(npKey(u))) continue;
        if (u.kind === 'service' && u.serviceId) serviceIds.push(u.serviceId);
        if (u.kind === 'package' && u.packageId) packageIds.push(u.packageId);
      }
      if (serviceIds.length === 0 && packageIds.length === 0) {
        setNpError('Select at least one service to resume.');
        return;
      }
    }
    setNpBusy(all ? 'all' : 'selected');
    setNpError(null);
    setNpFlash(null);
    try {
      const res = await api.admin.resumeNonPaymentServices(clientId, all ? {} : { serviceIds, packageIds });
      await Promise.all([reloadAll(), reloadNonPayment()]);
      onChanged?.();
      // 04 #118: name what actually came back. The server reports per-kind counts, and they can be
      // LOWER than what was submitted when a unit is blocked by a withdrawn catalog entry (04 #112),
      // so echoing the request rather than the response would overstate the outcome.
      const parts: string[] = [];
      if (res.servicesChanged > 0) {
        parts.push(`${res.servicesChanged} service${res.servicesChanged === 1 ? '' : 's'}`);
      }
      if (res.packagesChanged > 0) {
        parts.push(`${res.packagesChanged} package${res.packagesChanged === 1 ? '' : 's'}`);
      }
      setNpFlash(
        parts.length > 0
          ? `Resumed ${parts.join(' and ')}. The client has been notified.`
          : 'Nothing was resumed. Anything turned off in the service catalog stays suspended until you re-enable it there.',
      );
    } catch (e) {
      setNpError(apiErrorMessage(e));
    } finally {
      setNpBusy(null);
    }
  };

  useEffect(() => {
    let alive = true;
    setItems(null);
    setPackages(null);
    setError(null);
    setNpUnits([]);
    setNpSelected(new Set());
    setNpError(null);
    setNpFlash(null);
    setEditingId(null);
    setEditForm(null);
    setAssignId('');
    setPkgEditingId(null);
    setPkgEditForm(null);
    setAssignPkgId('');
    setSwitchId(null);
    void (async () => {
      try {
        const [list, cat, pkgs, pkgCat] = await Promise.all([
          api.admin.listClientServices(clientId),
          api.admin.listServices(),
          api.admin.listClientPackages(clientId),
          api.admin.listPackages(),
        ]);
        if (!alive) return;
        setItems(list.items);
        setRevivable(list.revivable ?? []);
        setClientBusinessId(list.businessId ?? null);
        setCatalog(cat.services);
        setPackages(pkgs.items);
        setPkgCatalog(pkgCat.packages);
        try {
          const np = await api.admin.listNonPaymentSuspended(clientId);
          if (alive) setNpUnits(np.units);
        } catch {
          if (alive) setNpUnits([]);
        }
      } catch (e) {
        if (alive) setError(apiErrorMessage(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [clientId]);

  // Standalone services = rows NOT owned by a package; package children show under their package.
  const standaloneItems = (items ?? []).filter((it) => !it.clientService.packageRef);
  // Assignable (standalone) = globally-active services not already on this client in ANY form
  // (active/suspended standalone OR a package child — all are present in `items`), and available to
  // this client's brand (s30 item 6; unknown brand id ⇒ unfiltered — the server enforces on assign).
  const assignable = (catalog ?? []).filter(
    (s) =>
      s.active &&
      !(items ?? []).some((it) => it.clientService.serviceId === s.id) &&
      (clientBusinessId == null || s.availableToBusinessIds == null || s.availableToBusinessIds.includes(clientBusinessId)),
  );

  const assignedPkgIds = new Set((packages ?? []).map((p) => p.clientPackage.packageId));
  const assignablePackages = pkgCatalog.filter((p) => p.active && !assignedPkgIds.has(p.id));

  // ── service handlers ──
  const doAssign = async (): Promise<void> => {
    if (!assignId) return;
    const run = async (): Promise<void> => {
      setAssignBusy(true);
      setError(null);
      setSeatNotice(null);
      try {
        const res = await api.admin.assignClientService(clientId, { serviceId: assignId });
        if (res.seatWarning) setSeatNotice(res.seatWarning.message);
        if (res.licenseExpensePrompt) setLicensePrompt(res.licenseExpensePrompt);
        noteImmediate(res.immediateInvoice);
        setAssignId('');
        await reloadItems();
        onChanged?.();
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setAssignBusy(false);
      }
    };
    // s30 item 5: assigning a service this client CANCELLED long ago REVIVES the old row (invariant
    // #1 / Fix 2) — fire the same data-loss caveat as a long-suspended reactivate, worded by the
    // service's retention model. Data Retainable revives silently (nothing to warn about).
    const rev = revivable.find((r) => r.serviceId === assignId);
    if (rev) {
      const days = staleDays(rev.lastActiveAt);
      const model = (catalog ?? []).find((c) => c.id === assignId)?.retentionModel ?? null;
      if (days !== null && days >= PURGE_THRESHOLD_DAYS && model !== 'data-retainable') {
        setReactivateCaveat({ days, kind: 'service', retention: model, revive: true, run });
        return;
      }
    }
    await run();
  };

  const openEdit = (it: ClientServiceWithCatalog): void => {
    const cs = it.clientService;
    setEditingId(cs.id);
    setEditForm({
      price: cs.priceOverride != null ? String(cs.priceOverride) : '',
      cost: cs.costOverride != null ? String(cs.costOverride) : '',
      billingEntity: cs.billingEntityOverride ?? '',
      suppress: cs.suppressCategoryAlternatives,
      autoCancelDays: cs.autoCancelAfterSuspendDaysOverride != null ? String(cs.autoCancelAfterSuspendDaysOverride) : '',
      termEndsAt: cs.termEndsAt ? cs.termEndsAt.slice(0, 10) : '',
    });
  };

  const closeEdit = (): void => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = async (id: string): Promise<void> => {
    if (!editForm) return;
    const priceTrim = editForm.price.trim();
    const costTrim = editForm.cost.trim();
    if (priceTrim !== '' && !(Number(priceTrim) >= 0)) {
      setError('Price override must be a number ≥ 0 (or blank to use the catalog price).');
      return;
    }
    if (costTrim !== '' && !(Number(costTrim) >= 0)) {
      setError('Cost override must be a number ≥ 0 (or blank to use the catalog cost).');
      return;
    }
    const autoTrim = editForm.autoCancelDays.trim();
    if (autoTrim !== '' && !(Number.isInteger(Number(autoTrim)) && Number(autoTrim) >= 1)) {
      setError('Auto-cancel override must be a whole number of days, at least 1 (or blank to inherit).');
      return;
    }
    setRowBusy(id);
    setError(null);
    try {
      // Always send all four override fields so a blanked field clears the override (engine uses
      // 'field' in body); a value sets it.
      await api.admin.updateClientService(clientId, id, {
        priceOverride: priceTrim === '' ? null : Number(priceTrim),
        costOverride: costTrim === '' ? null : Number(costTrim),
        billingEntityOverride: editForm.billingEntity === '' ? null : editForm.billingEntity,
        suppressCategoryAlternatives: editForm.suppress,
        // lifecycle (s30 item 5): blank clears (inherit / unset)
        autoCancelAfterSuspendDaysOverride: autoTrim === '' ? null : Number(autoTrim),
        termEndsAt: editForm.termEndsAt.trim() === '' ? null : editForm.termEndsAt.trim(),
      });
      closeEdit();
      await reloadItems();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setRowBusy(null);
    }
  };

  // Long-inactive re-enable caveat (04 #11): a service/package suspended past this window may have had
  // its data purged provider-side; reactivating restores access but not necessarily the old data.
  const PURGE_THRESHOLD_DAYS = 30;
  const staleDays = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const t = Date.parse(iso);
    return Number.isNaN(t) ? null : Math.floor((Date.now() - t) / 86_400_000);
  };

  const toggleStatus = async (it: ClientServiceWithCatalog): Promise<void> => {
    const cs = it.clientService;
    const next = cs.status === 'active' ? 'suspended' : 'active';
    const run = async (): Promise<void> => {
      setRowBusy(cs.id);
      setError(null);
      try {
        const res = await api.admin.updateClientService(clientId, cs.id, { status: next });
        noteImmediate(res.immediateInvoice);
        await reloadItems();
        onChanged?.();
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setRowBusy(null);
      }
    };
    // Reactivating after a long suspension → confirm the data-loss caveat first (fix-spec #10),
    // worded by the service's retention model (s30 item 5). Data Retainable reactivates silently.
    if (next === 'active') {
      const days = staleDays(cs.lastSyncAt ?? cs.enabledAt);
      const model = it.service.retentionModel ?? null;
      if (days !== null && days >= PURGE_THRESHOLD_DAYS && model !== 'data-retainable') {
        setReactivateCaveat({ days, kind: 'service', retention: model, revive: false, run });
        return;
      }
    }
    await run();
  };

  const doCancel = async (): Promise<void> => {
    if (!cancelTarget) return;
    setCancelBusy(true);
    setError(null);
    try {
      const res = await api.admin.cancelClientService(clientId, cancelTarget.clientService.id);
      setCancelTarget(null);
      await reloadItems();
      onChanged?.();
      if (res.pendingCredits) {
        setPendingTrigger('cancel');
        setPendingCredits(res.pendingCredits);
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setCancelBusy(false);
    }
  };

  // ── package handlers ──
  const doAssignPkg = async (): Promise<void> => {
    if (!assignPkgId) return;
    setAssignPkgBusy(true);
    setError(null);
    setSeatNotice(null);
    try {
      const res = await api.admin.assignClientPackage(clientId, { packageId: assignPkgId });
      if (res.seatWarnings && res.seatWarnings.length > 0) {
        setSeatNotice(res.seatWarnings.map((w) => w.message).join(' '));
      }
      noteImmediate(res.immediateInvoice);
      setAssignPkgId('');
      await reloadAll();
      onChanged?.();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setAssignPkgBusy(false);
    }
  };

  const openPkgEdit = (p: ClientPackageWithContents): void => {
    const cp = p.clientPackage;
    setPkgEditingId(cp.id);
    setSwitchId(null);
    setPkgEditForm({
      price: cp.priceOverride != null ? String(cp.priceOverride) : '',
      seats: String(cp.seats),
      billingEntity: cp.billingEntityOverride ?? '',
    });
  };

  const closePkgEdit = (): void => {
    setPkgEditingId(null);
    setPkgEditForm(null);
  };

  const savePkgEdit = async (id: string): Promise<void> => {
    if (!pkgEditForm) return;
    const priceTrim = pkgEditForm.price.trim();
    const seats = Number(pkgEditForm.seats.trim());
    if (priceTrim !== '' && !(Number(priceTrim) >= 0)) {
      setError('Price override must be a number ≥ 0 (or blank to use the package price).');
      return;
    }
    if (!Number.isInteger(seats) || seats < 1) {
      setError('Seats must be a whole number ≥ 1.');
      return;
    }
    setPkgRowBusy(id);
    setError(null);
    setSeatNotice(null);
    try {
      const res = await api.admin.updateClientPackage(clientId, id, {
        priceOverride: priceTrim === '' ? null : Number(priceTrim),
        seats,
        billingEntityOverride: pkgEditForm.billingEntity === '' ? null : pkgEditForm.billingEntity,
      });
      if (res.seatWarnings && res.seatWarnings.length > 0) {
        setSeatNotice(res.seatWarnings.map((w) => w.message).join(' '));
      }
      closePkgEdit();
      await reloadAll(); // seats propagate to the contained services
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setPkgRowBusy(null);
    }
  };

  const togglePkgStatus = async (p: ClientPackageWithContents): Promise<void> => {
    const cp = p.clientPackage;
    const next = cp.status === 'active' ? 'suspended' : 'active';
    const run = async (): Promise<void> => {
      setPkgRowBusy(cp.id);
      setError(null);
      try {
        const res = await api.admin.updateClientPackage(clientId, cp.id, { status: next });
        noteImmediate(res.immediateInvoice);
        await reloadAll(); // status cascades to the contained services
        onChanged?.();
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setPkgRowBusy(null);
      }
    };
    // Reactivating after a long suspension → confirm the data-loss caveat first (fix-spec #10),
    // worded by the WORST retention model among the stale contained services (s30 item 5): any
    // Deletion Required child gets the strong wording; if every stale child is Data Retainable the
    // package reactivates silently.
    if (next === 'active') {
      const freshest = p.children.reduce<number | null>((min, c) => {
        const d = staleDays(c.clientService.lastSyncAt ?? c.clientService.enabledAt);
        return d === null ? min : min === null ? d : Math.min(min, d);
      }, null);
      if (freshest !== null && freshest >= PURGE_THRESHOLD_DAYS) {
        const staleModels = p.children
          .filter((c) => {
            const d = staleDays(c.clientService.lastSyncAt ?? c.clientService.enabledAt);
            return d !== null && d >= PURGE_THRESHOLD_DAYS;
          })
          .map((c) => c.service.retentionModel ?? null);
        const worst: RetentionModel | null = staleModels.includes('deletion-required')
          ? 'deletion-required'
          : staleModels.includes(null)
            ? null
            : 'data-retainable';
        if (worst !== 'data-retainable') {
          setReactivateCaveat({ days: freshest, kind: 'package', retention: worst, revive: false, run });
          return;
        }
      }
    }
    await run();
  };

  const openSwitch = (p: ClientPackageWithContents): void => {
    setSwitchId(p.clientPackage.id);
    setSwitchToId('');
    setPkgEditingId(null);
  };
  const closeSwitch = (): void => {
    setSwitchId(null);
    setSwitchToId('');
  };
  const doSwitch = async (id: string): Promise<void> => {
    if (!switchToId) return;
    setPkgRowBusy(id);
    setError(null);
    try {
      const res = await api.admin.updateClientPackage(clientId, id, { switchToPackageId: switchToId });
      noteImmediate(res.immediateInvoice);
      closeSwitch();
      await reloadAll();
      onChanged?.();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setPkgRowBusy(null);
    }
  };

  const doRemovePkg = async (): Promise<void> => {
    if (!removePkgTarget) return;
    setRemovePkgBusy(true);
    setError(null);
    try {
      // Default dispositions: adopted children revert to standalone, package-created children cancel.
      const res = await api.admin.removeClientPackage(clientId, removePkgTarget.clientPackage.id);
      setRemovePkgTarget(null);
      await reloadAll();
      onChanged?.();
      if (res.pendingCredits) {
        setPendingTrigger('package-remove');
        setPendingCredits(res.pendingCredits);
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setRemovePkgBusy(false);
    }
  };

  // ── one-time credential handoff (08 Q32) — issue / void ──
  const openCredModal = (it: ClientServiceWithCatalog): void => {
    const bag = readHandoff(it.clientService);
    setCredTarget(it);
    setCredUsername(bag?.username ?? '');
    setCredPassword('');
    setCredDays('7');
    setCredError(null);
  };
  const closeCredModal = (): void => {
    // The password must never linger in state once the modal goes away.
    setCredTarget(null);
    setCredUsername('');
    setCredPassword('');
    setCredError(null);
  };
  const doIssueCredential = async (): Promise<void> => {
    if (!credTarget) return;
    const cs = credTarget.clientService;
    setCredBusy(true);
    setCredError(null);
    try {
      await api.admin.issueClientServiceCredential(clientId, cs.id, {
        username: credUsername.trim(),
        password: credPassword,
        expiresInDays: Number(credDays) || 7,
      });
      closeCredModal();
      // 04 #37: children render from the packages list, standalone rows from items — refresh both
      // so an issue on either kind of row updates its status fragment immediately.
      await Promise.all([reloadItems(), reloadPackages()]);
    } catch (e) {
      setCredError(apiErrorMessage(e));
      setCredPassword('');
    } finally {
      setCredBusy(false);
    }
  };
  const doVoidCredential = async (): Promise<void> => {
    if (!voidCredTarget) return;
    setVoidCredBusy(true);
    try {
      await api.admin.voidClientServiceCredential(clientId, voidCredTarget.clientService.id);
      setVoidCredTarget(null);
      await Promise.all([reloadItems(), reloadPackages()]);
    } catch (e) {
      setError(apiErrorMessage(e));
      setVoidCredTarget(null);
    } finally {
      setVoidCredBusy(false);
    }
  };

  // n39: the s49 rowBtn copy is retired; row actions ride the shared PillButton. Tones follow
  // the s51 ladder plus the fork E rule (a destructive row whose danger-confirm dialog exists
  // stays neutral; the dialog carries the tone) and the owner ruling Suspend=amber,
  // Reactivate=green.
  const fieldCls = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-2 py-1 text-sm bg-[var(--surface-solid)]';

  return (
    <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-4">
      {error ? <p className="text-sm text-[var(--tint-red-text)]">{error}</p> : null}
      {seatNotice ? (
        <div className="flex items-start justify-between gap-3 rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] px-3 py-2 text-sm text-[var(--tint-warn-text)]">
          <span>{seatNotice}</span>
          <button type="button" className="text-xs font-medium underline" onClick={() => setSeatNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {licenseCreated ? (
        <div className="flex items-start justify-between gap-3 rounded-md border border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)] px-3 py-2 text-sm text-[var(--tint-green-text)]">
          <span>{licenseCreated}</span>
          <button type="button" className="text-xs font-medium underline" onClick={() => setLicenseCreated(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      {/* 04 #118: the resume completion signal, a SIBLING of the card below. On success the card
          unmounts (npUnits empties), so anything rendered inside it would never be seen. */}
      <SaveFlash message={npFlash} onClear={() => setNpFlash(null)} />
      <SaveFlash message={immediateFlash} onClear={() => setImmediateFlash(null)} />

      {npUnits.length > 0 ? (
        <div className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] px-3 py-3 text-sm text-[var(--tint-warn-text)] space-y-2">
          <p className="font-medium">Suspended for non-payment</p>
          <p className="text-xs text-[var(--tint-warn-text)]">
            These services and packages were suspended by the non-payment cascade. Resume the ones you want back on (for
            example after writing off or voiding the overdue invoice). Reactivating restores access, reprovisions, and
            notifies the client. Anything turned off in the catalog stays suspended until you re-enable it there, so it is
            listed below but cannot be selected.
          </p>
          <ul className="space-y-1">
            {npUnits.map((u) => {
              {/* 04 #112: a blocked unit is shown, greyed and non-selectable, with the reason beside it.
                  The server refuses it either way, including under Resume all, which sends no ids. */}
              const blocked = !!u.blockedReason;
              return (
                <li key={npKey(u)} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id={`np-${npKey(u)}`}
                    checked={!blocked && npSelected.has(npKey(u))}
                    disabled={!!npBusy || blocked}
                    onChange={() => toggleNp(npKey(u))}
                    className="mt-0.5"
                  />
                  <label
                    htmlFor={`np-${npKey(u)}`}
                    className={blocked ? 'text-[var(--color-muted)]' : 'cursor-pointer'}
                  >
                    {u.name}
                    {blocked ? (
                      <span className="block text-xs text-[var(--color-muted)]">{u.blockedReason}</span>
                    ) : null}
                  </label>
                </li>
              );
            })}
          </ul>
          {npError ? <p className="text-xs text-[var(--tint-red-text)]">{npError}</p> : null}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {/* 04 #118 + 02d #39 lock 4 (reactivate/resume is GREEN, the `positive` role). `loading`
                is the shipped Button pending state: spinner, label hidden but width held, cursor-wait. */}
            <Button
              type="button"
              variant="positive"
              loading={npBusy === 'selected'}
              disabled={!!npBusy || npSelected.size === 0}
              onClick={() => void doResume(false)}
            >
              Resume selected
            </Button>
            <Button
              type="button"
              variant="positive"
              loading={npBusy === 'all'}
              disabled={!!npBusy}
              onClick={() => void doResume(true)}
            >
              Resume all
            </Button>
          </div>
        </div>
      ) : null}

      {/* ===================== PACKAGES ===================== */}
      <section>
        <p className="text-sm font-medium mb-2">Client packages</p>
        {!packages ? <p className="text-sm text-[var(--color-muted)]">Loading…</p> : null}
        {packages && packages.length === 0 ? (
          <p className="text-sm text-[var(--color-muted)]">No packages assigned yet.</p>
        ) : null}

        {packages && packages.length > 0 ? (
          <ul className="divide-y divide-[rgb(var(--edge-rgb)/0.05)]">
            {packages.map((p) => {
              const cp = p.clientPackage;
              const price = cp.priceOverride ?? p.package.defaultPrice;
              const suspended = cp.status === 'suspended';
              const editing = pkgEditingId === cp.id;
              const switching = switchId === cp.id;
              const busy = pkgRowBusy === cp.id;
              return (
                <li key={cp.id} className="py-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {p.package.name}
                        {suspended ? (
                          <span className="ml-2 rounded-full bg-[var(--tint-warn-bg)] px-2 py-0.5 text-xs text-[var(--tint-warn-text)]">suspended</span>
                        ) : null}
                      </p>
                      <p className="text-xs text-[var(--color-muted)] truncate">
                        {money(price)}
                        {cp.priceOverride != null ? <span className="text-[var(--ink-brand)]"> (custom)</span> : null}
                        <span className="mx-1.5">·</span>
                        {cp.seats} seat{cp.seats === 1 ? '' : 's'}
                        <span className="mx-1.5">·</span>
                        {p.children.length} service{p.children.length === 1 ? '' : 's'}
                        <span className="mx-1.5">·</span>
                        assigned {cp.enabledAt.slice(0, 10)}
                      </p>
                      {p.children.length > 0 ? (
                        // 04 #37 (the Q32 v1-cut follow-up): children are no longer a bare name list —
                        // each temp-password manual child gets the SAME issue/void surface as a
                        // standalone row (the API is row-based and already handles children; the modal
                        // and handlers are reused unchanged). Other children render as plain names.
                        <ul className="mt-1 space-y-0.5">
                          {p.children.map((c) => {
                            const ccs = c.clientService;
                            const csvc = c.service;
                            const childEligible = csvc.ssoType === 'none' && csvc.manualAccessMethod === 'temp-password';
                            const childBag = childEligible ? readHandoff(ccs) : null;
                            const childSt = childBag ? credState(childBag) : null;
                            return (
                              <li key={ccs.id} className="flex items-center justify-between gap-2 text-xs text-[var(--color-muted)]">
                                <span className="min-w-0 truncate">
                                  {csvc.name}
                                  {childBag && childSt === 'pending' ? (
                                    <> · sign-in details waiting (expires {childBag.expiresAt.slice(0, 10)})</>
                                  ) : null}
                                  {childBag && childSt === 'revealed' ? (
                                    <>
                                      {' '}· sign-in details viewed {(childBag.consumedAt ?? '').slice(0, 10)}
                                      {childBag.attestedAt ? ' · client confirmed changed' : ''}
                                    </>
                                  ) : null}
                                  {childBag && childSt === 'expired' ? (
                                    <> · sign-in details expired {childBag.expiresAt.slice(0, 10)}</>
                                  ) : null}
                                </span>
                                {childEligible && ccs.status === 'active' ? (
                                  <span className="shrink-0 flex items-center gap-1.5">
                                    <PillButton onClick={() => openCredModal(c)} disabled={busy}>
                                      Sign-in details…
                                    </PillButton>
                                    {childSt === 'pending' ? (
                                      <PillButton onClick={() => setVoidCredTarget(c)} disabled={busy}>
                                        Void details
                                      </PillButton>
                                    ) : null}
                                  </span>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                    </div>
                    <div className="shrink-0 flex flex-wrap items-center justify-end gap-1.5">
                      <PillButton onClick={() => (editing ? closePkgEdit() : openPkgEdit(p))} disabled={busy}>
                        {editing ? 'Close' : 'Edit'}
                      </PillButton>
                      <PillButton onClick={() => (switching ? closeSwitch() : openSwitch(p))} disabled={busy}>
                        Switch
                      </PillButton>
                      <PillButton tone={suspended ? 'green' : 'amber'} onClick={() => void togglePkgStatus(p)} disabled={busy}>
                        {suspended ? 'Reactivate' : 'Suspend'}
                      </PillButton>
                      <PillButton onClick={() => setRemovePkgTarget(p)} disabled={busy}>
                        Remove
                      </PillButton>
                    </div>
                  </div>

                  {editing && pkgEditForm ? (
                    <div className="mt-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] glass-flat p-3 space-y-2">
                      <p className="text-xs text-[var(--color-muted)]">
                        Overrides for this client only. Leave price blank to use the package price
                        {p.package.pricingMode === 'margin' ? ' (this package is margin-priced)' : ''}. Seats apply to all
                        contained services.
                      </p>
                      <div className="grid gap-2 sm:grid-cols-3">
                        <label className="block">
                          <span className="block text-xs font-medium mb-1">Price override ($)</span>
                          <input
                            value={pkgEditForm.price}
                            onChange={(e) => setPkgEditForm({ ...pkgEditForm, price: e.target.value })}
                            onKeyDown={stopEnter}
                            type="number"
                            step="any"
                            placeholder={`pkg ${money(p.package.defaultPrice)}`}
                            className={fieldCls}
                          />
                        </label>
                        <label className="block">
                          <span className="block text-xs font-medium mb-1">Seats</span>
                          <input
                            value={pkgEditForm.seats}
                            onChange={(e) => setPkgEditForm({ ...pkgEditForm, seats: e.target.value })}
                            onKeyDown={stopEnter}
                            type="number"
                            step="1"
                            min="1"
                            className={fieldCls}
                          />
                        </label>
                        <label className="block">
                          <span className="block text-xs font-medium mb-1">Billing entity</span>
                          <select
                            value={pkgEditForm.billingEntity}
                            onChange={(e) => setPkgEditForm({ ...pkgEditForm, billingEntity: e.target.value })}
                            className={fieldCls}
                          >
                            <option value="">Package default ({brandName(p.package.defaultBillingEntity)})</option>
                            {businesses.map((b) => (
                              <option key={b.id} value={b.id}>
                                {brandDisplayName(b.name)}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <Button type="button" onClick={() => void savePkgEdit(cp.id)} loading={busy}>
                          Save overrides
                        </Button>
                        <Button type="button" variant="ghost" onClick={closePkgEdit} disabled={busy}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  {switching ? (
                    <div className="mt-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] glass-flat p-3 space-y-2">
                      <p className="text-xs text-[var(--color-muted)]">
                        Switch to another package. Services in both stay; services only in the old package follow their
                        default removal (adopted → standalone, package-only → cancelled); new services are added.
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={switchToId}
                          onChange={(e) => setSwitchToId(e.target.value)}
                          className={`${fieldCls} max-w-xs`}
                        >
                          <option value="">Choose a package…</option>
                          {pkgCatalog
                            .filter((x) => x.active && x.id !== cp.packageId)
                            .map((x) => (
                              <option key={x.id} value={x.id}>
                                {x.name} · {money(x.defaultPrice)}
                              </option>
                            ))}
                        </select>
                        <Button type="button" onClick={() => void doSwitch(cp.id)} loading={busy} disabled={!switchToId}>
                          Switch
                        </Button>
                        <Button type="button" variant="ghost" onClick={closeSwitch} disabled={busy}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        {/* Assign a package */}
        {pkgCatalog.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select
              value={assignPkgId}
              onChange={(e) => setAssignPkgId(e.target.value)}
              onKeyDown={stopEnter}
              disabled={assignablePackages.length === 0 || assignPkgBusy}
              className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            >
              <option value="">{assignablePackages.length === 0 ? 'No more packages to assign' : 'Add a package…'}</option>
              {assignablePackages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {money(p.defaultPrice)} · {p.serviceIds.length} service{p.serviceIds.length === 1 ? '' : 's'}
                </option>
              ))}
            </select>
            <Button type="button" onClick={() => void doAssignPkg()} loading={assignPkgBusy} disabled={!assignPkgId}>
              Assign package
            </Button>
          </div>
        ) : null}
      </section>

      {/* ===================== SERVICES ===================== */}
      <section className="border-t border-[rgb(var(--edge-rgb)/0.10)] pt-4">
        <p className="text-sm font-medium mb-2">Client services</p>
        {!items ? <p className="text-sm text-[var(--color-muted)]">Loading…</p> : null}

        {items && standaloneItems.length === 0 ? (
          <p className="text-sm text-[var(--color-muted)]">No standalone services assigned yet.</p>
        ) : null}

        {items && standaloneItems.length > 0 ? (
          <ul className="divide-y divide-[rgb(var(--edge-rgb)/0.05)]">
            {standaloneItems.map((it) => {
              const cs = it.clientService;
              const svc = it.service;
              const price = cs.priceOverride ?? svc.defaultClientPrice;
              const cost = cs.costOverride ?? svc.defaultCostToYou;
              const entityId = cs.billingEntityOverride ?? svc.defaultBillingEntity;
              const suspended = cs.status === 'suspended';
              const editing = editingId === cs.id;
              const busy = rowBusy === cs.id;
              // one-time credential handoff (08 Q32): only a temp-password manual-access service
              const credEligible = svc.ssoType === 'none' && svc.manualAccessMethod === 'temp-password';
              const credBag = credEligible ? readHandoff(cs) : null;
              const credSt = credBag ? credState(credBag) : null;
              return (
                <li key={cs.id} className="py-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {svc.name}
                        {suspended ? (
                          <span className="ml-2 rounded-full bg-[var(--tint-warn-bg)] px-2 py-0.5 text-xs text-[var(--tint-warn-text)]">suspended</span>
                        ) : null}
                      </p>
                      <p className="text-xs text-[var(--color-muted)] truncate">
                        {money(price)}
                        {cs.priceOverride != null ? <span className="text-[var(--ink-brand)]"> (custom)</span> : null}
                        <span className="mx-1.5">·</span>
                        cost {money(cost)}
                        {cs.costOverride != null ? ' (custom)' : ''}
                        <span className="mx-1.5">·</span>
                        {brandName(entityId)}
                        {cs.billingEntityOverride != null ? ' (custom)' : ''}
                        <span className="mx-1.5">·</span>
                        assigned {cs.enabledAt.slice(0, 10)}
                        {(svc.billingTerm ?? 'monthly') === 'annual' && cs.termEndsAt ? (
                          <>
                            <span className="mx-1.5">·</span>
                            billed through {cs.termEndsAt.slice(0, 10)}
                          </>
                        ) : null}
                        {credBag && credSt === 'pending' ? (
                          <>
                            <span className="mx-1.5">·</span>
                            sign-in details waiting (expires {credBag.expiresAt.slice(0, 10)})
                          </>
                        ) : null}
                        {credBag && credSt === 'revealed' ? (
                          <>
                            <span className="mx-1.5">·</span>
                            sign-in details viewed {(credBag.consumedAt ?? '').slice(0, 10)}
                            {credBag.attestedAt ? ' · client confirmed changed' : ''}
                          </>
                        ) : null}
                        {credBag && credSt === 'expired' ? (
                          <>
                            <span className="mx-1.5">·</span>
                            sign-in details expired {credBag.expiresAt.slice(0, 10)}
                          </>
                        ) : null}
                      </p>
                    </div>
                    <div className="shrink-0 flex flex-wrap items-center justify-end gap-1.5">
                      <PillButton onClick={() => (editing ? closeEdit() : openEdit(it))} disabled={busy}>
                        {editing ? 'Close' : 'Edit'}
                      </PillButton>
                      {credEligible && !suspended ? (
                        <PillButton onClick={() => openCredModal(it)} disabled={busy}>
                          Sign-in details…
                        </PillButton>
                      ) : null}
                      {credEligible && !suspended && credSt === 'pending' ? (
                        <PillButton onClick={() => setVoidCredTarget(it)} disabled={busy}>
                          Void details
                        </PillButton>
                      ) : null}
                      <PillButton tone={suspended ? 'green' : 'amber'} onClick={() => void toggleStatus(it)} disabled={busy}>
                        {suspended ? 'Reactivate' : 'Suspend'}
                      </PillButton>
                      <PillButton onClick={() => setCancelTarget(it)} disabled={busy}>
                        Cancel
                      </PillButton>
                    </div>
                  </div>

                  {editing && editForm ? (
                    <div className="mt-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] glass-flat p-3 space-y-2">
                      <p className="text-xs text-[var(--color-muted)]">
                        Overrides for this client only. Leave a field blank to use the catalog value
                        {svc.pricingMode === 'margin' ? ' (this service is margin-priced)' : ''}. Assigned by admin ·
                        provisioning: {cs.provisioningState}.
                      </p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="block">
                          <span className="block text-xs font-medium mb-1">Price override ($)</span>
                          <input
                            value={editForm.price}
                            onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                            onKeyDown={stopEnter}
                            type="number"
                            step="any"
                            placeholder={`catalog ${money(svc.defaultClientPrice)}`}
                            className={fieldCls}
                          />
                        </label>
                        <label className="block">
                          <span className="block text-xs font-medium mb-1">Cost override ($)</span>
                          <input
                            value={editForm.cost}
                            onChange={(e) => setEditForm({ ...editForm, cost: e.target.value })}
                            onKeyDown={stopEnter}
                            type="number"
                            step="any"
                            placeholder={`catalog ${money(svc.defaultCostToYou)}`}
                            className={fieldCls}
                          />
                        </label>
                        <label className="block">
                          <span className="block text-xs font-medium mb-1">Billing entity</span>
                          <select
                            value={editForm.billingEntity}
                            onChange={(e) => setEditForm({ ...editForm, billingEntity: e.target.value })}
                            className={fieldCls}
                          >
                            <option value="">Catalog default ({brandName(svc.defaultBillingEntity)})</option>
                            {businesses.map((b) => (
                              <option key={b.id} value={b.id}>
                                {brandDisplayName(b.name)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block">
                          <span className="block text-xs font-medium mb-1">Auto-cancel override (days)</span>
                          <input
                            value={editForm.autoCancelDays}
                            onChange={(e) => setEditForm({ ...editForm, autoCancelDays: e.target.value })}
                            onKeyDown={stopEnter}
                            type="number"
                            step="1"
                            placeholder={
                              svc.autoCancelAfterSuspendDays != null
                                ? `catalog ${svc.autoCancelAfterSuspendDays} days`
                                : 'catalog: never'
                            }
                            className={fieldCls}
                          />
                        </label>
                        {(svc.billingTerm ?? 'monthly') === 'annual' ? (
                          <label className="block">
                            <span className="block text-xs font-medium mb-1">Billed through (annual term)</span>
                            <input
                              value={editForm.termEndsAt}
                              onChange={(e) => setEditForm({ ...editForm, termEndsAt: e.target.value })}
                              onKeyDown={stopEnter}
                              type="date"
                              className={fieldCls}
                            />
                          </label>
                        ) : null}
                        <label className="flex items-center gap-2 sm:mt-6">
                          <input
                            type="checkbox"
                            checked={editForm.suppress}
                            onChange={(e) => setEditForm({ ...editForm, suppress: e.target.checked })}
                          />
                          <span className="text-xs">Suppress category alternatives</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <Button type="button" onClick={() => void saveEdit(cs.id)} loading={busy}>
                          Save overrides
                        </Button>
                        <Button type="button" variant="ghost" onClick={closeEdit} disabled={busy}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        {/* Assign a new service */}
        {catalog ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-3">
            <select
              value={assignId}
              onChange={(e) => setAssignId(e.target.value)}
              onKeyDown={stopEnter}
              disabled={assignable.length === 0 || assignBusy}
              className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            >
              <option value="">{assignable.length === 0 ? 'No more services to assign' : 'Add a service…'}</option>
              {assignable.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {money(s.defaultClientPrice)} · {brandName(s.defaultBillingEntity)}
                </option>
              ))}
            </select>
            <Button type="button" onClick={() => void doAssign()} loading={assignBusy} disabled={!assignId}>
              Assign
            </Button>
          </div>
        ) : null}
      </section>

      <ConfirmDialog
        open={cancelTarget !== null}
        title="Cancel this service?"
        confirmLabel="Cancel service"
        cancelLabel="Keep service"
        confirmVariant="danger"
        busy={cancelBusy}
        onConfirm={() => void doCancel()}
        onCancel={() => setCancelTarget(null)}
      >
        <p>
          Cancel <strong>{cancelTarget?.service.name}</strong> for this client? The assignment is kept for billing
          history (soft-cancel) but removed from the active list. You can re-assign it later.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={removePkgTarget !== null}
        title="Remove this package?"
        confirmLabel="Remove package"
        cancelLabel="Keep package"
        confirmVariant="danger"
        busy={removePkgBusy}
        onConfirm={() => void doRemovePkg()}
        onCancel={() => setRemovePkgTarget(null)}
      >
        <p>
          Remove <strong>{removePkgTarget?.package.name}</strong> from this client? Services that were adopted into the
          package revert to standalone; services the package added are cancelled (soft-cancel, kept for billing history).
          You can re-assign the package later.
        </p>
      </ConfirmDialog>

      {/* 08 Q32: issue one-time sign-in details (temp-password handoff) */}
      <ConfirmDialog
        open={credTarget !== null}
        title="Issue sign-in details"
        confirmLabel="Issue"
        cancelLabel="Cancel"
        confirmVariant="primary"
        busy={credBusy}
        confirmDisabled={!credUsername.trim() || !credPassword}
        onConfirm={() => void doIssueCredential()}
        onCancel={closeCredModal}
      >
        <p className="text-xs text-[var(--color-muted)]">
          Stored once in the secure vault and shown to the client exactly once. It can never be viewed
          again, including by you. If details are lost, issue new ones.
          {credTarget && readHandoff(credTarget.clientService) &&
          credState(readHandoff(credTarget.clientService)!) === 'pending'
            ? ' Issuing replaces the pending, unviewed details.'
            : ''}
        </p>
        {credError ? <p className="text-xs text-[var(--tint-red-text)]">{credError}</p> : null}
        <label className="block">
          <span className="block text-xs font-medium mb-1">Username (vendor sign-in name)</span>
          <input
            value={credUsername}
            onChange={(e) => setCredUsername(e.target.value)}
            onKeyDown={stopEnter}
            maxLength={256}
            className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-2 py-1 text-sm bg-[var(--surface-solid)] font-mono"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium mb-1">Temporary password (set the same value at the vendor)</span>
          <div className="flex gap-2">
            <input
              value={credPassword}
              onChange={(e) => setCredPassword(e.target.value)}
              onKeyDown={stopEnter}
              maxLength={256}
              autoComplete="off"
              className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-2 py-1 text-sm bg-[var(--surface-solid)] font-mono"
            />
            <Button type="button" variant="ghost" onClick={() => setCredPassword(generatePassword())}>
              Generate
            </Button>
          </div>
        </label>
        <label className="block">
          <span className="block text-xs font-medium mb-1">Reveal window (days, 1 to 30)</span>
          <input
            value={credDays}
            onChange={(e) => setCredDays(e.target.value)}
            onKeyDown={stopEnter}
            type="number"
            min="1"
            max="30"
            step="1"
            className="w-24 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-2 py-1 text-sm bg-[var(--surface-solid)]"
          />
        </label>
      </ConfirmDialog>

      {/* 08 Q32: void a pending, unrevealed handoff */}
      <ConfirmDialog
        open={voidCredTarget !== null}
        title="Void these sign-in details?"
        confirmLabel="Void details"
        cancelLabel="Keep them"
        confirmVariant="danger"
        busy={voidCredBusy}
        onConfirm={() => void doVoidCredential()}
        onCancel={() => setVoidCredTarget(null)}
      >
        <p>
          The pending sign-in details for <strong>{voidCredTarget?.service.name}</strong> will be voided and the client
          will no longer be able to view them. Issue new ones whenever you are ready.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={reactivateCaveat !== null}
        title={reactivateCaveat?.kind === 'package' ? 'Reactivate this package?' : 'Reactivate this service?'}
        confirmLabel="Reactivate"
        cancelLabel="Cancel"
        confirmVariant="primary"
        onConfirm={() => {
          const c = reactivateCaveat;
          setReactivateCaveat(null);
          if (c) void c.run();
        }}
        onCancel={() => setReactivateCaveat(null)}
      >
        {reactivateCaveat ? (
          <p>
            {reactivateCaveat.kind === 'package' ? "This package's services were" : 'This service was'}
            {reactivateCaveat.revive ? ' previously cancelled and' : ''} last active about {reactivateCaveat.days} days
            ago.{' '}
            {reactivateCaveat.retention === 'deletion-required'
              ? `This ${reactivateCaveat.kind === 'package' ? 'package includes a service' : 'service is'} marked Deletion Required: the provider may have permanently deleted stored data during that time. Reactivating restores access, but it cannot restore data that was permanently deleted.`
              : `The provider may have purged ${reactivateCaveat.kind === 'package' ? 'their' : 'its'} data during that time. Reactivating restores access, but anything previously stored there may not come back.`}
          </p>
        ) : null}
      </ConfirmDialog>

      <PendingCreditsDialog
        pending={pendingCredits}
        trigger={pendingTrigger}
        clientName={clientName ?? 'this client'}
        onClose={() => setPendingCredits(null)}
        onResolved={(res, refunds) => {
          setPendingCredits(null);
          if (res) setImmediateFlash(resolvedSummary(res));
          onChanged?.();
          // The paid-source branch: the Invoices page's refund dialog, pre-filled (one source invoice at a time).
          if (refunds.length > 0) navigate(refundDeepLink(clientId, refunds[0]));
        }}
      />

      <CreateLicenseExpenseDialog
        prompt={licensePrompt}
        businesses={businesses}
        onCreated={() => {
          const name = licensePrompt?.serviceName ?? 'the service';
          setLicensePrompt(null);
          setLicenseCreated(`Linked license expense created for ${name}. Its cost now derives from seats in use (see Expenses).`);
        }}
        onClose={() => setLicensePrompt(null)}
      />
    </div>
  );
}
